import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Plus, Trash2, BadgeCheck, FileClock, ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const empty = { state: "", category: "", requirement: "", care_setting: "", org_type: "", worker_role: "", payer: "", training_hours: "", initial_annual: "", renewal_frequency: "", credential_type: "", required_document: "", source: "", agency: "", source_url: "", effective_date: "" };

// Defined at module scope so React keeps input identity stable (no focus loss on keystroke).
function Field({ label, k, ph, form, setForm }) {
  return <div><Label>{label}</Label><Input data-testid={`req-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" placeholder={ph} /></div>;
}
function Picker({ label, k, items, req, form, setForm }) {
  return (
    <div><Label>{label}{req && " *"}</Label>
      <Select value={form[k]} onValueChange={(v) => setForm({ ...form, [k]: v })}>
        <SelectTrigger data-testid={`req-${k}`} className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="max-h-72">{items.map((o) => <SelectItem key={o.id || o.slug} value={o.id || o.slug}>{o.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function AdminCompliance() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tax, setTax] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterState, setFilterState] = useState("");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [verifyOnImport, setVerifyOnImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(() => {
    const q = filterState ? `?state=${filterState}` : "";
    api.get(`/admin/compliance/requirements${q}`).then((r) => setRows(r.data)).catch(() => {});
  }, [filterState]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading || !user || user.role !== "admin") return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/compliance/requirements", form);
      toast.success("Requirement added as draft — verify it to publish"); setForm(empty); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };
  const verify = async (id) => { try { await api.post(`/admin/compliance/requirements/${id}/verify`); toast.success("Verified & published"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Add a source + URL first"); } };
  const remove = async (id) => { await api.delete(`/admin/compliance/requirements/${id}`); toast.success("Deleted"); load(); };

  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const rd = new FileReader(); rd.onload = () => setCsvText(String(rd.result)); rd.readAsText(f); };
  const runImport = async () => {
    if (!csvText.trim()) { toast.error("Paste or upload CSV first"); return; }
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post("/admin/compliance/requirements/import", { csv: csvText, verify: verifyOnImport });
      setImportResult(data); toast.success(`Imported ${data.created} (${data.verified} verified)`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const catName = (id) => tax?.categories.find((c) => c.id === id)?.name || id;
  const stName = (slug) => tax?.states.find((s) => s.slug === slug)?.name || slug;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-blue-800 mb-4 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> Admin</Link>
      <div className="flex items-center gap-3 mb-8">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-900 text-white"><ShieldCheck className="w-6 h-6" /></span>
        <div><h1 className="font-display text-3xl font-bold text-blue-950">Regulatory Data Manager</h1><p className="text-slate-500">Enter, source and verify requirements. Only verified records go public.</p></div>
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-8">
        <form onSubmit={create} data-testid="req-form" className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm space-y-3 self-start">
          <h3 className="font-display font-semibold text-blue-950 flex items-center gap-2"><Plus className="w-4 h-4" /> Add requirement</h3>
          {tax && <>
            <Picker label="State" k="state" items={tax.states} req form={form} setForm={setForm} />
            <Picker label="Category" k="category" items={tax.categories} req form={form} setForm={setForm} />
            <div><Label>Requirement *</Label><Textarea data-testid="req-requirement" rows={2} value={form.requirement} onChange={(e) => setForm({ ...form, requirement: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <Picker label="Care setting" k="care_setting" items={tax.care_settings} form={form} setForm={setForm} />
              <Picker label="Org type" k="org_type" items={tax.org_types} form={form} setForm={setForm} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Picker label="Worker role" k="worker_role" items={tax.worker_roles} form={form} setForm={setForm} />
              <Picker label="Payer" k="payer" items={tax.payer_types} form={form} setForm={setForm} />
            </div>
            <div className="grid grid-cols-2 gap-2"><Field label="Training hours" k="training_hours" ph="e.g. 75" form={form} setForm={setForm} /><Field label="Initial/Annual" k="initial_annual" ph="initial | annual" form={form} setForm={setForm} /></div>
            <div className="grid grid-cols-2 gap-2"><Field label="Renewal freq." k="renewal_frequency" ph="e.g. every 2 yrs" form={form} setForm={setForm} /><Field label="Credential type" k="credential_type" form={form} setForm={setForm} /></div>
            <Field label="Required document" k="required_document" form={form} setForm={setForm} />
            <div className="pt-2 border-t border-blue-900/5"><p className="text-xs font-semibold text-slate-500 mb-2">Source (required to verify)</p>
              <div className="space-y-3">
                <Field label="Source name" k="source" ph="e.g. Ohio Dept of Health" form={form} setForm={setForm} />
                <Field label="Regulating agency" k="agency" form={form} setForm={setForm} />
                <Field label="Source URL" k="source_url" ph="https://…" form={form} setForm={setForm} />
                <Field label="Effective date" k="effective_date" ph="YYYY-MM-DD" form={form} setForm={setForm} />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="req-save" className="w-full bg-blue-900 hover:bg-blue-950 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add as Draft"}</Button>
          </>}
        </form>

        <div>
          <div className="bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm mb-6" data-testid="csv-import">
            <h3 className="font-display font-semibold text-blue-950 mb-1">Bulk CSV import</h3>
            <p className="text-xs text-slate-500 mb-3">Columns: <code className="text-teal-700">state, category, requirement, source, source_url</code> (+ optional care_setting, org_type, worker_role, payer, training_hours, initial_annual, renewal_frequency, credential_type, required_document, agency, effective_date).</p>
            <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="csv-file" className="text-sm mb-2 block" />
            <Textarea data-testid="csv-text" rows={4} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="Paste CSV here (with header row)…" className="font-mono text-xs" />
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm text-blue-950"><input type="checkbox" data-testid="csv-verify" checked={verifyOnImport} onChange={(e) => setVerifyOnImport(e.target.checked)} /> Verify rows that include a source + URL</label>
              <Button onClick={runImport} disabled={importing} data-testid="csv-import-btn" className="bg-teal-600 hover:bg-teal-700 text-white">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}</Button>
            </div>
            {importResult && (
              <div data-testid="csv-result" className="mt-3 text-sm bg-slate-50 rounded-lg p-3">
                <p className="text-blue-950 font-medium">Created {importResult.created} · Verified {importResult.verified} · Skipped {importResult.skipped}</p>
                {importResult.errors?.length > 0 && <ul className="mt-1 text-xs text-red-600 list-disc pl-5">{importResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Select value={filterState || "all"} onValueChange={(v) => setFilterState(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="req-filter-state" className="w-56"><SelectValue placeholder="All states" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="all">All states</SelectItem>{tax?.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-sm text-slate-500">{rows.length} record{rows.length !== 1 ? "s" : ""}</span>
          </div>
          {rows.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-blue-900/5"><FileClock className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No requirements entered yet. Add one to begin.</p></div>
          ) : (
            <div className="space-y-3" data-testid="req-list">
              {rows.map((r) => (
                <div key={r.id} data-testid={`req-row-${r.id}`} className="bg-white rounded-xl border border-blue-900/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-teal-700">{stName(r.state)}</span>
                        <span className="text-xs text-slate-400">·</span>
                        <span className="text-xs text-slate-500">{catName(r.category)}</span>
                        {r.internal_review_status === "verified"
                          ? <span className="text-[11px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Verified</span>
                          : <span className="text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><FileClock className="w-3 h-3" /> Draft</span>}
                      </div>
                      <p className="text-sm text-blue-950 mt-1.5">{r.requirement}</p>
                      <p className="text-xs text-slate-400 mt-1">{r.source || "No source"} {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-teal-700 underline ml-1">link</a>} {r.last_verified && `· verified ${r.last_verified}`}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {r.internal_review_status !== "verified" && <Button size="sm" data-testid={`req-verify-${r.id}`} onClick={() => verify(r.id)} className="bg-teal-600 hover:bg-teal-700 text-white gap-1 h-8"><BadgeCheck className="w-4 h-4" /> Verify</Button>}
                      <button onClick={() => remove(r.id)} data-testid={`req-delete-${r.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
